import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfileAvatar } from "../ProfileAvatar";
import { User, Briefcase, Mail, Save, Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";

interface MyProfileTabProps {
  initialData: any;
}

export const MyProfileTab = ({ initialData }: MyProfileTabProps) => {
  const { user } = useAuth();
  const { refetchProfile } = useProfile();
  const { toast } = useToast();
  const [avatarUrl, setAvatarUrl] = useState(initialData?.avatar_url);

  const form = useForm({
    defaultValues: {
      full_name: initialData?.full_name || "",
      location: initialData?.location || "",
      phone_number: initialData?.phone_number || "",
      job_title: initialData?.job_title || "",
      calendly_url: initialData?.calendly_url || "",
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user?.id);

      if (error) throw error;

      // Refresh the profile context to get the updated data
      await refetchProfile();

      toast({
        title: "Profile updated successfully",
      });
      
      // Reset form dirty state
      form.reset(data);
    } catch (error) {
      toast({
        title: "Error updating profile",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Profile Picture Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <User className="h-5 w-5 text-primary" />
                Profile Picture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileAvatar
                avatarUrl={avatarUrl}
                setAvatarUrl={setAvatarUrl}
                userInitial={initialData?.full_name?.[0] || "U"}
              />
            </CardContent>
          </Card>

          {/* Personal Information Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <User className="h-5 w-5 text-primary" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Full Name</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter your full name" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Location</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="City, Country" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Phone Number</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="+1 (555) 123-4567" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        type="tel"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <Label className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email Address
                </Label>
                <div className="p-3 border border-border rounded-lg bg-muted/50">
                  <p className="text-foreground text-sm">{initialData?.email || "Not set"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information Card */}
          <Card className="border border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Briefcase className="h-5 w-5 text-primary" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Job Title</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Your job title" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="calendly_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      Calendly URL (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="https://calendly.com/your-username" 
                        className="h-11 border border-input bg-background transition-colors focus:border-ring"
                        type="url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-center pt-6">
            <Button 
              type="submit" 
              size="lg"
              className="gap-2 px-8 py-3 h-12 bg-secondary hover:bg-secondary-hover text-secondary-foreground font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              disabled={form.formState.isSubmitting || !form.formState.isDirty}
            >
              <Save className="h-4 w-4" />
              {form.formState.isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};